import {ClientUserState} from '../../../../shared/scripts/ClientUserState'
import {setElementInnerHTML, setElementOnClick, setImageElementAlt} from '../../../../shared/scripts/ui_utils/DomUtils'
import {UserState} from '../../../../../shared/UserState'
import {getLobbyImagePath} from '../../../../shared/scripts/ui_utils/Utils'
import {LobbyImage} from '@colonist/types'
import {ViewPopupLogin} from '../../../../shared/scripts/ui_popup/ui_popup_membership_incentive/ViewPopupLogin'
import {TranslationObject} from '@colonist/i18n'
import {ViewPopupEditProfile} from '../../../../shared/scripts/ui_popup/ViewPopupEditProfile'
import {PlayerColor} from '@colonist/game-states'
import {ProfileEditAjaxEndPoints} from '../../../../shared/scripts/profile/ProfileEditAjaxEndPoints'
import {UIWProfileUsernameController} from './UIWProfileUsernameController'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../../shared/entity_datas/EntityDatas'
import {CA} from '../../../../shared/scripts/analytics/Analytics'
import {AudioManager, AudioType} from '@colonist/audio'

interface DiscordUserStateOverrides {
    username: string
    profilePictureUrl: string
    icon: LobbyImage
    karmaStatus: TranslationObject
}

export class UIHeaderProfileController {
    private readonly usernameDivs: NodeListOf<HTMLParagraphElement>
    private readonly karmaDivs: NodeListOf<HTMLParagraphElement>
    private readonly playerIconImgs: NodeListOf<HTMLImageElement>
    private readonly membershipIconImgs: NodeListOf<HTMLImageElement>
    private readonly loginButtons: HTMLCollectionOf<HTMLAnchorElement>
    private readonly loggedInContainers: NodeListOf<HTMLDivElement>
    private readonly visitorContainers: NodeListOf<HTMLDivElement>
    private readonly visitorNameDivs: NodeListOf<HTMLParagraphElement>

    private _discordUserStateOverrides?: DiscordUserStateOverrides
    get discordUserStateOverrides(): DiscordUserStateOverrides | undefined {
        return this._discordUserStateOverrides
    }
    set discordUserStateOverrides(value: DiscordUserStateOverrides) {
        this._discordUserStateOverrides = value
        this.updateProfile(ClientUserState.userState)
    }

    constructor() {
        this.usernameDivs = document.getElementsByName('web-header-username') as NodeListOf<HTMLParagraphElement>
        this.karmaDivs = document.getElementsByName('web-header-karma') as NodeListOf<HTMLParagraphElement>
        this.playerIconImgs = document.getElementsByName('web-header-profile-player-icon') as NodeListOf<HTMLImageElement>
        this.membershipIconImgs = document.getElementsByName('web-header-profile-membership-icon') as NodeListOf<HTMLImageElement>
        this.loginButtons = document.getElementsByClassName('web-header-login-button') as HTMLCollectionOf<HTMLAnchorElement>
        this.loggedInContainers = document.getElementsByName('web-header-profile-loggedin-container') as NodeListOf<HTMLDivElement>
        this.visitorContainers = document.getElementsByName('web-header-visitor-container') as NodeListOf<HTMLDivElement>
        this.visitorNameDivs = document.getElementsByName('web-header-visitor-name') as NodeListOf<HTMLParagraphElement>

        this.initProfileView()

        ClientUserState.registerChangeListener((userState) => {
            if(userState == undefined) return
            this.updateProfile(userState)
        })

        this.initLoginButton()
        this.initProfileClick()
    }

    private initProfileView() {
        const userState = ClientUserState.userState
        if(!userState) {
            this.loggedInContainers.forEach((container) => container.classList.add('hidden'))
            this.visitorContainers.forEach((container) => container.classList.remove('hidden'))
            this.visitorNameDivs.forEach((div) => div.classList.add('hidden'))
            return
        }

        const hasVisitorName = !userState.isLoggedIn && userState.username != undefined

        this.loggedInContainers.forEach((container) => container.classList.toggle('hidden', !userState.isLoggedIn))
        this.visitorContainers.forEach((container) => container.classList.toggle('hidden', userState.isLoggedIn))
        this.visitorNameDivs.forEach((div) => div.classList.toggle('hidden', !hasVisitorName))
    }

    initLoginButton(pageIdentifier?: 'lobby_page' | 'main_page') {
        Array.from(this.loginButtons).forEach((button) => {
            setElementOnClick(button, () => {
                AudioManager.play(AudioType.Click)
                new ViewPopupLogin()

                void CA.sendAnalyticEvent({
                    type: AnalyticEventType.ClickedHeaderLogin,
                    category: AnalyticEventCategory.Header,
                    stringValue: pageIdentifier ?? window.location.pathname,
                })
            })
        })
    }

    private initProfileClick() {
        this.loggedInContainers.forEach((container) => {
            setElementOnClick(container, () => {
                if(!ClientUserState.userState?.isLoggedIn) return

                AudioManager.play(AudioType.Click)
                new ViewPopupEditProfile({
                    usernameController: UIWProfileUsernameController,
                    selectColorAction: async(color: PlayerColor) => await ProfileEditAjaxEndPoints.updatePreferredColor(color),
                })
            })
        })
    }

    private updateProfile(userState: UserState | undefined) {
        if(!userState) return

        const username = this.discordUserStateOverrides?.username ?? userState.username
        if(username) {
            this.usernameDivs.forEach((div) => {
                setElementInnerHTML(div, {key: 'strings:colonistCommon.utils.#buffer', options: {value: username}})
            })
        }

        const karmaStatus = this.discordUserStateOverrides?.karmaStatus ?? userState.getPlayerKarmaStatusText()
        this.karmaDivs.forEach((div) => {
            setElementInnerHTML(div, karmaStatus)
        })

        const profilePictureUrl = this.discordUserStateOverrides?.profilePictureUrl ?? userState.profilePictureUrl
        if(profilePictureUrl) {
            this.playerIconImgs.forEach((img) => {
                img.src = profilePictureUrl
                img.style.borderRadius = '100%'
            })
        } else {
            const icon = this.discordUserStateOverrides?.icon ?? userState.icon ?? LobbyImage.Guest
            const path = getLobbyImagePath(icon)
            this.playerIconImgs.forEach((img) => {
                img.src = path
                setImageElementAlt(img, icon)
            })
        }

        if(userState.isLoggedIn) {
            const currentMembership = userState.items.getCurrentMembership()
            if(currentMembership != undefined) {
                const membershipPath = getLobbyImagePath(currentMembership.image)
                this.membershipIconImgs.forEach((img) => {
                    img.src = membershipPath
                    img.classList.remove('hidden')
                    if(currentMembership.pending) {
                        img.style.filter = 'grayscale(1)'
                    }
                })
            }
        }

        const hasVisitorName = !userState.isLoggedIn && userState.username != undefined

        this.loggedInContainers.forEach((container) => container.classList.toggle('hidden', !userState.isLoggedIn))
        this.visitorContainers.forEach((container) => container.classList.toggle('hidden', userState.isLoggedIn))
        this.visitorNameDivs.forEach((div) => div.classList.toggle('hidden', !hasVisitorName))

        if(hasVisitorName) {
            this.visitorNameDivs.forEach((div) => {
                setElementInnerHTML(div, {key: 'strings:colonistCommon.utils.#buffer', options: {value: userState.username}})
            })
        }
    }
}
